// src/components/manuscriptTheme/themeSections.js
//
// The Theme Editor's left panel, as data: one entry per accordion section, one
// row per control. A port of the prototype's SECTIONS, less two of its sections
// — trim size (the manuscript's own, set in the editor) and the title and
// copyright pages (the Manuscript Structure page's, so page art does not repeat
// on them either).
//
// A row is `[type, path, label, ...]`:
//   range  — [min, max, step, unit, showIf?]
//   select — [options, showIf?]
//   seg    — [options, showIf?]          options are [value, label] pairs
//   toggle — [showIf?]
//   font   — [showIf?]
//   glyph  — [options, showIf?]          options are [value, label]
//   color  — [swatches]
//   head   — a sub-heading: ['head', text, showIf?]
//   note   — a line of help: ['note', text, showIf?]
//   dyn    — help worked out from the theme: ['dyn', (t) => ({ tone, text }), showIf?]
//   artpick, chips, anchor — the page-art picker, page-kind chips, anchor grid
//
// `focus` names what the preview outlines and jumps to while the section is open.

import {
  BREAK_STYLES, CASES, LABEL_STYLES, ORNAMENTS, THEME_LIMITS as L,
} from '../../config/manuscriptThemes';
import { BLEED_IN, artName, artOn, chapterLabel } from '../../utils/manuscriptTheme';

const labelOn = (t) => t.ch.label !== 'none';
const runOn = (t) => t.run.mode !== 'none';
const folioShown = (t) => t.run.mode !== 'manuscript';

// Said wherever the preview shows something the printed PDF does not do yet
// (backend/utils/manuscript_theme.py lists why).
const NOT_PRINTED = 'Shown here; the printed PDF does not do this yet.';

const trimOf = (t) => t.trimInches || { w: 6, h: 9 };
const fmtIn = (v) => v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');

/** The prototype's bleed note, for the extent the art is set to. */
function bleedNote(t) {
  const a = t.art;
  if (a.extent === 'bleed') {
    const { w, h } = trimOf(t);
    return {
      tone: 'good',
      text: `Full bleed: the art runs ${BLEED_IN}″ past the trim on the outside edge, top and bottom, so nothing white shows after cutting. The printed PDF becomes ${fmtIn(w + BLEED_IN)} × ${fmtIn(h + 2 * BLEED_IN)} in, the size printers expect. Keep key details inside the green safe line (Bleed guides, above the preview).`,
    };
  }
  if (a.extent === 'trim') {
    return { tone: 'warn', text: '⚠ Art stops exactly at the trim. Cutting can drift slightly, which may leave thin white slivers. Use Full bleed for printed books.' };
  }
  if (a.extent === 'margins') {
    return { tone: 'plain', text: 'Art sits inside the text block and moves with the gutter on left and right pages. No bleed needed.' };
  }
  return {
    tone: a.inset < 0.25 ? 'warn' : 'plain',
    text: a.inset < 0.25
      ? 'No bleed needed. Keep the inset at 0.25″ or more so trimming never touches the art.'
      : 'No bleed needed. This inset is safely clear of the trim.',
  };
}

/** The prototype's note on how strong art behind text should be. */
function readNote(t) {
  const a = t.art;
  if (a.pages.body && a.opacity > 30 && a.extent !== 'inset') {
    return { tone: 'warn', text: `⚠ At ${a.opacity}% behind body text, art can tire readers over a long book. Full-page textures read best at 5–20%. Save stronger art for chapter openers.` };
  }
  return { tone: 'plain', text: 'Tip: chapter openers can carry bolder art (30–80%). Body pages read best with subtle textures (5–20%).' };
}

export const SECTIONS = [
  {
    id: 'presets', title: 'Theme presets', icon: 'palette', focus: null,
    sum: (t) => t.name || 'Untitled theme',
  },
  {
    id: 'page', title: 'Page & layout', icon: 'page', focus: 'page',
    sum: (t) => `Margins ${t.page.top}″ / ${t.page.inside}″ gutter`,
    rows: [
      ['range', 'page.top', 'Top margin', ...L.page.top, 0.05, 'in'],
      ['range', 'page.bottom', 'Bottom margin', ...L.page.bottom, 0.05, 'in'],
      ['range', 'page.inside', 'Inside margin (gutter)', ...L.page.inside, 0.05, 'in'],
      ['range', 'page.outside', 'Outside margin', ...L.page.outside, 0.05, 'in'],
      ['toggle', 'ch.recto', 'Start every chapter on a right-hand page'],
      ['note', `Right-hand chapter starts: ${NOT_PRINTED}`, (t) => t.ch.recto],
      ['note', 'Dashed outlines in the preview show the text block. Changing the margins re-flows every chapter. The page size is the manuscript’s own — it is set in the editor’s Page Setup.'],
    ],
  },
  {
    id: 'body', title: 'Body text', icon: 'text', focus: 'body',
    sum: (t) => `${t.body.font} · ${t.body.size}/${(t.body.size * t.body.lead).toFixed(1)} pt`,
    rows: [
      ['font', 'body.font', 'Typeface'],
      ['range', 'body.size', 'Size', ...L.body.size, 0.5, 'pt'],
      ['range', 'body.lead', 'Line spacing', ...L.body.lead, 0.02, '×'],
      ['seg', 'body.align', 'Alignment', [['justify', 'Justified'], ['left', 'Ragged right']]],
      ['seg', 'body.style', 'Paragraphs', [['indent', 'First-line indent'], ['spaced', 'Space between']]],
      ['range', 'body.indent', 'Indent size', ...L.body.indent, 0.25, 'em', (t) => t.body.style === 'indent'],
      ['toggle', 'body.hyph', 'Hyphenation'],
      ['note', `Hyphenation: ${NOT_PRINTED}`, (t) => t.body.hyph],
    ],
  },
  {
    id: 'chapter', title: 'Chapter openers', icon: 'chapter', focus: 'chapter',
    sum: (t) => `${chapterLabel(t.ch.label, 1) || 'No label'} · ${t.ch.tFont}`,
    rows: [
      ['head', 'Chapter label'],
      ['select', 'ch.label', 'Label style', LABEL_STYLES],
      ['note', 'Off by default: most manuscripts already say “Chapter 1” in the heading, and the label would print it twice.', (t) => !labelOn(t)],
      ['font', 'ch.lFont', 'Label typeface', labelOn],
      ['range', 'ch.lSize', 'Label size', ...L.ch.lSize, 0.5, 'pt', labelOn],
      ['seg', 'ch.lCase', 'Label case', CASES, labelOn],
      ['range', 'ch.lTrack', 'Label letter spacing', ...L.ch.lTrack, 0.01, 'em', labelOn],
      ['head', 'Chapter title'],
      ['font', 'ch.tFont', 'Title typeface'],
      ['range', 'ch.tSize', 'Title size', ...L.ch.tSize, 0.5, 'pt'],
      ['seg', 'ch.tWeight', 'Weight', [[400, 'Regular'], [600, 'Semibold'], [700, 'Bold']]],
      ['seg', 'ch.tCase', 'Case', CASES],
      ['toggle', 'ch.tItalic', 'Italic title'],
      ['head', 'Opening page'],
      ['seg', 'ch.align', 'Alignment', [['left', 'Left'], ['center', 'Centered']]],
      ['range', 'ch.sink', 'Drop from top of page', ...L.ch.sink, 1, '%'],
      ['glyph', 'ch.orn', 'Ornament', ORNAMENTS],
      ['seg', 'ch.first', 'First paragraph', [['none', 'Plain'], ['smallcaps', 'Small caps'], ['dropcap', 'Drop cap'], ['both', 'Both']]],
      ['note', 'In the printed PDF the small caps cover the first few words rather than the whole first line.', (t) => ['smallcaps', 'both'].includes(t.ch.first)],
      ['range', 'ch.drop', 'Drop cap height', ...L.ch.drop, 1, 'lines', (t) => ['dropcap', 'both'].includes(t.ch.first)],
    ],
  },
  {
    id: 'headings', title: 'Scene headings', icon: 'head', focus: 'headings',
    sum: (t) => `${t.h.font} · ${t.h.size} pt`,
    rows: [
      ['font', 'h.font', 'Typeface'],
      ['range', 'h.size', 'Size', ...L.h.size, 0.5, 'pt'],
      ['seg', 'h.weight', 'Weight', [[400, 'Regular'], [500, 'Medium'], [600, 'Semibold'], [700, 'Bold']]],
      ['seg', 'h.case', 'Case', CASES],
      ['toggle', 'h.italic', 'Italic'],
      ['seg', 'h.align', 'Alignment', [['left', 'Left'], ['center', 'Centered']]],
      ['seg', 'h.color', 'Color', [['text', 'Text'], ['accent', 'Accent']]],
      ['note', 'Scene headings are the headings inside a chapter (Heading 2 to 4 in the editor). A Heading 1 is a chapter title.'],
    ],
  },
  {
    id: 'breaks', title: 'Scene breaks', icon: 'brk', focus: 'breaks',
    sum: (t) => (BREAK_STYLES.find(([k]) => k === t.brk.style) || [])[1],
    rows: [
      ['glyph', 'brk.style', 'Break style', BREAK_STYLES],
      ['seg', 'brk.after', 'First line after a break', [['none', 'Plain'], ['smallcaps', 'Small caps']]],
      ['note', 'Scene breaks use a printed ornament so they survive page turns. They are inserted as one element (the ··· button above the page), so changing the style here restyles every break in the book.'],
    ],
  },
  {
    id: 'quotes', title: 'Quotes & lists', icon: 'quote', focus: 'quotes',
    sum: (t) => `${t.q.italic ? 'Italic' : 'Roman'} quotes · ${({ bullet: '•', dash: '–', circle: '◦', arrow: '›' })[t.list.marker]} lists`,
    rows: [
      ['toggle', 'q.italic', 'Italic block quotes'],
      ['range', 'q.indent', 'Quote indent', ...L.q.indent, 0.25, 'em'],
      ['range', 'q.size', 'Quote size', ...L.q.size, 1, '%'],
      ['toggle', 'q.rule', 'Accent rule beside quotes'],
      ['seg', 'list.marker', 'List marker', [['bullet', '•'], ['dash', '–'], ['circle', '◦'], ['arrow', '›']]],
    ],
  },
  {
    id: 'running', title: 'Running heads & page numbers', icon: 'run', focus: 'running',
    sum: (t) => `${({ book: 'Author | Title', chapter: 'Title | Chapter', manuscript: 'Manuscript header', none: 'No running head' })[t.run.mode]} · ${({ 'bottom-center': 'folio bottom', 'bottom-outside': 'folio outside', 'top-outside': 'folio top', none: 'no folio' })[t.folio.pos]}`,
    rows: [
      ['select', 'run.mode', 'Running head', [['none', 'None'], ['book', 'Author (left) · Book title (right)'], ['chapter', 'Book title (left) · Chapter (right)'], ['manuscript', 'Manuscript · Surname / TITLE / page']]],
      ['note', `Running heads: ${NOT_PRINTED}`, runOn],
      ['seg', 'run.role', 'Typeface', [['body', 'Body'], ['label', 'Label'], ['heading', 'Heading']], runOn],
      ['seg', 'run.case', 'Case', CASES, runOn],
      ['range', 'run.size', 'Size', ...L.run.size, 0.5, 'pt'],
      ['range', 'run.track', 'Letter spacing', ...L.run.track, 0.01, 'em', runOn],
      ['head', 'Page numbers'],
      ['select', 'folio.pos', 'Position', [['bottom-center', 'Bottom · centered'], ['bottom-outside', 'Bottom · outside corner'], ['top-outside', 'Top · outside, beside running head'], ['none', 'None']], folioShown],
      ['seg', 'folio.style', 'Style', [['plain', '7'], ['dash', '— 7 —'], ['dot', '· 7 ·']], (t) => folioShown(t) && t.folio.pos !== 'none'],
      ['toggle', 'folio.opener', 'Number chapter-opening pages', (t) => folioShown(t) && t.folio.pos !== 'none'],
      ['note', `Leaving the number off opening pages: ${NOT_PRINTED}`, (t) => folioShown(t) && t.folio.pos !== 'none' && !t.folio.opener],
      ['note', 'Running heads are left off chapter openers and blank pages automatically, as in a professionally typeset book.'],
    ],
  },
  {
    id: 'art', title: 'Page art & bleed', icon: 'image', focus: 'art',
    sum: (t) => (artOn(t)
      ? `${artName(t.art)} · ${({ bleed: 'full bleed', trim: 'trim edge', margins: 'text block', inset: 'inset' })[t.art.extent]} · ${t.art.opacity}%`
      : 'Off'),
    rows: [
      ['toggle', 'art.on', 'Show page art'],
      ['artpick', 'art.src', 'Image'],
      ['chips', 'art.pages', 'Repeat on', [['opener', 'Chapter openers'], ['body', 'Body pages'], ['blank', 'Blank pages']], artOn],
      ['note', 'The title and copyright pages are the Structure page’s, so art does not go on them.', artOn],
      ['head', 'Placement', artOn],
      ['seg', 'art.extent', 'Extent', [['bleed', 'Full bleed'], ['trim', 'Trim edge'], ['margins', 'Text block'], ['inset', 'Inset']], artOn],
      ['range', 'art.inset', 'Inset from trim', 0.1, 1.5, 0.05, 'in', (t) => artOn(t) && t.art.extent === 'inset'],
      ['dyn', bleedNote, artOn],
      ['seg', 'art.fit', 'Fit', [['cover', 'Fill'], ['contain', 'Fit'], ['stretch', 'Stretch'], ['tile', 'Tile'], ['actual', 'Actual']], artOn],
      ['range', 'art.scale', 'Scale', 10, 300, 5, '%', (t) => artOn(t) && ['tile', 'actual'].includes(t.art.fit)],
      ['anchor', 'art.anchor', 'Anchor', (t) => artOn(t) && t.art.fit !== 'stretch'],
      ['toggle', 'art.mirror', 'Mirror on left-hand pages', artOn],
      ['head', 'Appearance', artOn],
      ['range', 'art.opacity', 'Opacity', 0, 100, 1, '%', artOn],
      ['select', 'art.blend', 'Blend with paper', [['multiply', 'Multiply · best for textures'], ['normal', 'Normal'], ['soft-light', 'Soft light'], ['overlay', 'Overlay'], ['darken', 'Darken'], ['luminosity', 'Luminosity'], ['screen', 'Screen']], artOn],
      ['note', 'Screen, overlay and soft light lighten the paper. A printed book is on white paper, where they leave nothing to see — the art will not print.', (t) => artOn(t) && ['screen', 'overlay', 'soft-light'].includes(t.art.blend)],
      ['seg', 'art.filter', 'Color treatment', [['none', 'Original'], ['grayscale', 'Grayscale'], ['sepia', 'Sepia']], artOn],
      ['range', 'art.blur', 'Soften (blur)', 0, 8, 0.5, 'px', artOn],
      ['select', 'art.fade', 'Edge fade', [['none', 'None'], ['vignette', 'Soft vignette'], ['bottom', 'Fade toward bottom'], ['top', 'Fade toward top'], ['gutter', 'Fade toward spine']], artOn],
      ['dyn', readNote, artOn],
    ],
  },
  {
    id: 'colors', title: 'Color', icon: 'color', focus: null,
    sum: () => 'Text · accent · paper',
    rows: [
      // design-tokens-allow — swatches of a book's colours: the author's data, not app chrome
      ['color', 'col.text', 'Text', ['#1d1a17', '#000000', '#2a2633', '#33302b']],
      // design-tokens-allow
      ['color', 'col.accent', 'Accent (labels, ornaments, drop caps)', ['#1d1a17', '#5b39c6', '#a3445d', '#c4262e', '#7a5a1e', '#2f6b5a', '#e0672f']],
      // design-tokens-allow
      ['color', 'col.paper', 'Paper', ['#ffffff', '#fffdf8', '#fbf6ea', '#f6efe0']],
      ['note', 'For print, keep the accent dark enough to read on paper. The printed PDF is on white: paper colour would print as ink over every page, so it shows here only.'],
    ],
  },
];

/** What the preview jumps to and outlines for each section's `focus`. */
export const FOCUS_SELECTOR = {
  page: '.rv-page-body',
  body: '.rv-page-body > p',
  chapter: '[data-heading-level="1"]',
  headings: '[data-heading-level="2"], [data-heading-level="3"], [data-heading-level="4"]',
  breaks: '.rv-scene-break',
  quotes: 'blockquote, ul',
  running: null,
  art: null,
};
